import React, { useState, useRef } from 'react';
import { Client } from '@gradio/client';
import './DetectionSection.css';

/* ── Hugging Face Space API ── */
const HF_API_URL = process.env.REACT_APP_HF_API_URL;

// ── Class names exactly matching model's class_indices.json ──────────────────
const diseaseDB = {
  // ── Tomato (10 classes) ───────────────────────────────────────────────────
  'Tomato___Bacterial_spot': {
    display: 'Bacterial Spot', severity: 'High', isHealthy: false,
    symptoms: 'Small dark brown spots with yellow halos on leaves. Raised scab-like lesions on fruit.',
    treatment: 'Copper-based bactericide spray karein. Wet conditions mein kaam se bachein. Crop rotation karein.',
  },
  'Tomato___Early_blight': {
    display: 'Early Blight', severity: 'Medium', isHealthy: false,
    symptoms: 'Circular brown spots with concentric rings on older leaves. Yellowing around lesions. Premature leaf drop.',
    treatment: 'Mancozeb ya Chlorothalonil spray karein har 7–10 din mein. Infected pattay hata dein.',
  },
  'Tomato___Late_blight': {
    display: 'Late Blight', severity: 'Critical', isHealthy: false,
    symptoms: 'Dark water-soaked lesions on leaves and stems. White fuzzy mold on leaf undersides.',
    treatment: 'Metalaxyl fungicide turant lagaein. Infected plants jala dein. Overhead irrigation band karein.',
  },
  'Tomato___Leaf_Mold': {
    display: 'Leaf Mold', severity: 'Medium', isHealthy: false,
    symptoms: 'Yellow patches on upper leaf surface with olive-green mold on underside. Leaves curl and drop.',
    treatment: 'Greenhouse ventilation improve karein. Chlorothalonil fungicide lagaein.',
  },
  'Tomato___Septoria_leaf_spot': {
    display: 'Septoria Leaf Spot', severity: 'Medium', isHealthy: false,
    symptoms: 'Small circular spots with dark borders and light gray centers. Tiny black dots visible inside spots.',
    treatment: 'Neeche ke infected pattay hata dein. Mancozeb fungicide spray karein. Mulch lagaein.',
  },
  'Tomato___Spider_mites Two-spotted_spider_mite': {
    display: 'Spider Mites', severity: 'Medium', isHealthy: false,
    symptoms: 'Tiny yellow speckling on leaves. Fine webbing on undersides. Leaves turn bronze then drop.',
    treatment: 'Insecticidal soap ya neem oil spray karein. Plants ko pani se dhoyein.',
  },
  'Tomato___Target_Spot': {
    display: 'Target Spot', severity: 'Medium', isHealthy: false,
    symptoms: 'Brown circular lesions with concentric rings resembling a target. Spreads from lower to upper leaves.',
    treatment: 'Azoxystrobin ya chlorothalonil fungicide spray karein. Infected debris hata dein.',
  },
  'Tomato___Tomato_Yellow_Leaf_Curl_Virus': {
    display: 'Yellow Leaf Curl Virus', severity: 'Critical', isHealthy: false,
    symptoms: 'Upward curling and yellowing of leaves. Stunted plant growth. Reduced fruit set.',
    treatment: 'Infected plants nikal dein. Whitefly control karein. Reflective mulch use karein.',
  },
  'Tomato___Tomato_mosaic_virus': {
    display: 'Tomato Mosaic Virus', severity: 'Critical', isHealthy: false,
    symptoms: 'Mosaic pattern of light and dark green patches on leaves. Distorted and curled young leaves.',
    treatment: 'Koi chemical ilaj nahi. Infected plants immediately hata dein. Tools disinfect karein.',
  },
  'Tomato___healthy': {
    display: 'Healthy Tomato', severity: 'None', isHealthy: true,
    symptoms: 'No visible disease symptoms. Leaf tissue appears normal and healthy.',
    treatment: 'Koi ilaj zaruri nahi! Fasal bilkul theek hai. Regular monitoring jaari rakhein.',
  },

  // ── Corn / Maize (4 classes) ──────────────────────────────────────────────
  'Corn_(maize)___Cercospora_leaf_spot Gray_leaf_spot': {
    display: 'Gray Leaf Spot (Cercospora)', severity: 'High', isHealthy: false,
    symptoms: 'Narrow rectangular tan-gray lesions running parallel to leaf veins. Lesions may merge causing large dead areas.',
    treatment: 'Strobilurin fungicide use karein. Resistant hybrid lagaein. Crop rotation karein.',
  },
  'Corn_(maize)___Common_rust_': {
    display: 'Common Rust', severity: 'Medium', isHealthy: false,
    symptoms: 'Small oval brick-red pustules on both leaf surfaces. Pustules rupture releasing orange spores.',
    treatment: 'Triazole ya strobilurin fungicide early stage mein lagaein. Resistant varieties lagaein.',
  },
  'Corn_(maize)___Northern_Leaf_Blight': {
    display: 'Northern Leaf Blight', severity: 'High', isHealthy: false,
    symptoms: 'Long cigar-shaped grayish-tan lesions 1–6 inches long. Dark borders. Lesions may coalesce.',
    treatment: 'Propiconazole ya azoxystrobin fungicide lagaein. Resistant hybrids lagaein.',
  },
  'Corn_(maize)___healthy': {
    display: 'Healthy Maize', severity: 'None', isHealthy: true,
    symptoms: 'No visible disease symptoms. Leaf tissue uniformly green and healthy.',
    treatment: 'Koi ilaj zaruri nahi! Fasal bilkul theek hai. Good agronomic practices jaari rakhein.',
  },
};

const SEVERITY_COLOR = { None: 'var(--green-accent)', Medium: 'var(--gold-light)', High: '#ff9f43', Critical: '#ff6b6b', Unknown: 'var(--text-muted)' };

export default function DetectionSection() {
  const [selectedCrop, setSelectedCrop] = useState('tomato');
  const [preview,      setPreview]      = useState(null);
  const [fileName,     setFileName]     = useState('');
  const [fileBlob,     setFileBlob]     = useState(null);
  const [dragging,     setDragging]     = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [result,       setResult]       = useState(null);
  const [barWidth,     setBarWidth]     = useState(0);
  const [error,        setError]        = useState('');
  const fileRef = useRef();

  const handleFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setFileName(file.name);
    setFileBlob(file);
    setResult(null);
    setBarWidth(0);
    setError('');
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const runDetection = async () => {
    if (!preview || !fileBlob) { alert('Pehle image upload karein.'); return; }
    setLoading(true);
    setResult(null);
    setBarWidth(0);
    setError('');

    try {
      /* ── Official Gradio Client — handles CORS automatically ── */
      const client = await Client.connect('gorinagorina332/cropguard-api');
      const response = await client.predict('/predict', { image: fileBlob });
      const output = response.data[0];

      if (!output || output.error) throw new Error(output?.error || 'No result');

      const disease    = output.disease || '';
      const confidence = Math.round(output.confidence || 0);
      const info       = diseaseDB[disease] || {
        display:   disease.replace(/___/g, ' ').replace(/_/g, ' '),
        symptoms:  'Detailed symptoms ke liye disease reference library dekhein.',
        treatment: output.treatment || 'Agricultural expert se milein.',
        severity:  output.severity  || 'Unknown',
        isHealthy: output.is_healthy || false,
      };

      setResult({ ...info, confidence, disease });
      setTimeout(() => setBarWidth(confidence), 150);

    } catch (err) {
      console.error('Detection error:', err);
      setError(`Detection fail ho gayi: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="detect">
      <div className="container">
        <div className="text-center mb-5 reveal">
          <span className="section-label">Detection Tool</span>
          <h2 className="section-title">
            Analyze Your <span className="accent">Crop Now</span>
          </h2>
          <p className="section-desc mx-auto">
            Upload a clear image of a tomato or maize leaf for instant AI-powered disease diagnosis.
          </p>
        </div>

        <div className="row justify-content-center reveal">
          <div className="col-lg-10">
            <div className="detect-box">

              {/* ── API Status Bar ── */}
              <div className="api-status-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="api-dot"></span>
                  <span style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>
                    Model: <strong style={{ color: 'var(--green-accent)' }}>
                      Connected — gorinagorina332/cropguard-api (HuggingFace)
                    </strong>
                  </span>
                </div>
                <span style={{ fontSize: '.75rem', color: 'var(--text-muted)', background: 'rgba(82,183,136,.08)', padding: '4px 10px', borderRadius: 20, border: '1px solid rgba(82,183,136,.15)' }}>
                  🟢 Live API · 14 Classes · 98% Accuracy
                </span>
              </div>

              <div className="row g-4">
                {/* ── Upload Column ── */}
                <div className="col-md-6">
                  <h5 style={{ color: 'var(--cream)', marginBottom: 20, fontFamily: 'Playfair Display,serif' }}>Upload Leaf Image</h5>

                  {preview ? (
                    <div>
                      <img src={preview} alt="preview" className="upload-preview-img" />
                      <button
                        onClick={() => { setPreview(null); setFileBlob(null); setResult(null); setBarWidth(0); setError(''); }}
                        style={{ background: 'rgba(82,183,136,.1)', border: '1px solid rgba(82,183,136,.25)', borderRadius: 8, color: 'var(--green-accent)', padding: '5px 14px', cursor: 'pointer', fontSize: '.82rem', fontFamily: 'DM Sans,sans-serif', marginBottom: 10 }}
                      >
                        <i className="bi bi-arrow-counterclockwise me-1"></i> Change Image
                      </button>
                    </div>
                  ) : (
                    <div
                      className={`upload-zone ${dragging ? 'dragging' : ''}`}
                      onClick={() => fileRef.current.click()}
                      onDragOver={e => { e.preventDefault(); setDragging(true); }}
                      onDragLeave={() => setDragging(false)}
                      onDrop={onDrop}
                    >
                      <input type="file" ref={fileRef} accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])} />
                      <div style={{ fontSize: '3rem', color: 'var(--green-accent)', marginBottom: 12 }}>🍃</div>
                      <div style={{ color: 'var(--cream)', fontWeight: 600, marginBottom: 6 }}>Drag & Drop ya Click karein</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '.85rem' }}>JPG, PNG — Tomato ya Maize leaf</div>
                    </div>
                  )}

                  {/* Crop selector */}
                  <div className="text-center mt-3">
                    <div style={{ marginBottom: 10, color: 'var(--text-muted)', fontSize: '.85rem' }}>Crop select karein:</div>
                    <span className={`filter-tab ${selectedCrop === 'tomato' ? 'active' : ''}`} onClick={() => setSelectedCrop('tomato')}>🍅 Tomato</span>
                    <span className={`filter-tab ${selectedCrop === 'maize'  ? 'active' : ''}`} onClick={() => setSelectedCrop('maize')}>🌽 Maize</span>
                  </div>

                  {/* Error */}
                  {error && (
                    <div style={{ marginTop: 12, background: 'rgba(255,80,80,.1)', border: '1px solid rgba(255,80,80,.25)', borderRadius: 10, padding: '10px 14px', color: '#ff8a8a', fontSize: '.82rem' }}>
                      <i className="bi bi-exclamation-circle me-2"></i>{error}
                    </div>
                  )}

                  {/* Analyze button */}
                  <button
                    className="btn-primary-green w-100 mt-4 justify-content-center"
                    onClick={runDetection}
                    disabled={loading || !preview}
                    style={{ borderRadius: 12, padding: '14px', opacity: (!preview || loading) ? .55 : 1, cursor: (!preview || loading) ? 'not-allowed' : 'pointer' }}
                  >
                    {loading
                      ? <><span style={{ width: 18, height: 18, border: '3px solid rgba(255,255,255,.3)', borderTopColor: '#fff', display: 'inline-block', borderRadius: '50%', animation: 'spin .7s linear infinite', marginRight: 8 }}></span>AI Analyze kar raha hai...</>
                      : <><i className="bi bi-cpu me-2"></i>Analyze Crop</>
                    }
                  </button>
                </div>

                {/* ── Results Column ── */}
                <div className="col-md-6">
                  <h5 style={{ color: 'var(--cream)', marginBottom: 20, fontFamily: 'Playfair Display,serif' }}>Detection Results</h5>

                  {loading && (
                    <div className="text-center" style={{ padding: '40px 0' }}>
                      <div className="spinner-custom mx-auto"></div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '.9rem', marginTop: 16 }}>
                        AI model image analyze kar raha hai...
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '.78rem', marginTop: 6, opacity: .6 }}>
                        Pehli baar 30–60 sec lag sakte hain (model load ho raha hai)
                      </div>
                    </div>
                  )}

                  {!loading && !result && !error && (
                    <div className="detect-idle">
                      <div style={{ fontSize: '3rem', color: 'rgba(82,183,136,.3)', marginBottom: 12 }}>📋</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '.9rem' }}>Image upload karein aur Analyze dabao</div>
                    </div>
                  )}

                  {!loading && result && (
                    <div className="result-card">
                      {/* Header */}
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        <div>
                          <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>Detected Disease</div>
                          <div style={{ fontFamily: 'Playfair Display,serif', fontSize: '1.2rem', color: 'var(--cream)', marginTop: 2 }}>{result.display}</div>
                        </div>
                        <div style={{
                          fontSize: '.75rem', padding: '5px 12px', borderRadius: 50, flexShrink: 0, marginLeft: 10,
                          background: result.isHealthy ? 'rgba(82,183,136,.15)' : 'rgba(255,107,107,.1)',
                          color:      result.isHealthy ? 'var(--green-accent)'  : '#ff6b6b',
                          border:     `1px solid ${result.isHealthy ? 'rgba(82,183,136,.3)' : 'rgba(255,107,107,.3)'}`,
                        }}>
                          {result.isHealthy ? '✓ Healthy' : '⚠ Beemari Mili'}
                        </div>
                      </div>

                      {/* Severity */}
                      {!result.isHealthy && (
                        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>Severity:</span>
                          <span style={{ fontSize: '.75rem', fontWeight: 700, color: SEVERITY_COLOR[result.severity] || 'var(--text-muted)', background: 'rgba(0,0,0,.2)', padding: '2px 10px', borderRadius: 20 }}>
                            {result.severity}
                          </span>
                        </div>
                      )}

                      {/* Confidence Bar */}
                      <div className="mb-4">
                        <div className="d-flex justify-content-between mb-1">
                          <span style={{ fontSize: '.82rem', color: 'var(--text-muted)' }}>AI Confidence</span>
                          <span style={{ fontSize: '.82rem', color: 'var(--green-accent)', fontWeight: 600 }}>{result.confidence}%</span>
                        </div>
                        <div className="confidence-bar-wrap">
                          <div className="confidence-bar" style={{ width: `${barWidth}%` }}></div>
                        </div>
                      </div>

                      {/* Symptoms */}
                      <div className="result-inner-box">
                        <div className="result-inner-label">🔍 Symptoms</div>
                        <div style={{ fontSize: '.88rem', color: 'var(--cream)', lineHeight: 1.65 }}>{result.symptoms}</div>
                      </div>

                      {/* Treatment */}
                      <div className="result-inner-box mb-0">
                        <div className="result-inner-label">💊 Recommended Treatment</div>
                        <div style={{ fontSize: '.88rem', color: 'var(--green-accent)', lineHeight: 1.65 }}>{result.treatment}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
